import { motion, useReducedMotion } from "framer-motion";
import { ArrowDown, CalendarCheck, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HERO_IMAGE } from "@/data/site";
import { buildWhatsAppUrl } from "@/lib/whatsapp";

const scrollTo = (id) => (e) => {
  e.preventDefault();
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Hero = () => {
  const reduce = useReducedMotion();
  const fade = (delay) => ({
    initial: reduce ? false : { opacity: 0, y: 18 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1], delay },
  });

  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative overflow-hidden bg-gradient-to-b from-[hsl(var(--brand-warm-white))] via-[hsl(var(--brand-off-white))] to-[hsl(var(--brand-warm-white))] pt-[72px]"
    >
      <div className="noise-overlay" aria-hidden="true" />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-32 top-24 h-[420px] w-[420px] rounded-full bg-[hsl(var(--brand-champagne-gold))]/15 blur-3xl"
      />

      <div className="container-site relative grid min-h-[calc(100vh-72px)] items-center gap-12 py-16 lg:grid-cols-12 lg:gap-8 lg:py-20">
        <div className="flex flex-col gap-7 lg:col-span-6">
          <motion.span {...fade(0)} className="eyebrow">
            Odontologia premium
          </motion.span>

          <motion.h1
            {...fade(0.08)}
            data-testid="hero-title"
            className="text-4xl font-semibold leading-[1.08] tracking-[-0.02em] text-foreground sm:text-5xl lg:text-6xl"
          >
            Seu sorriso, cuidado em cada detalhe.
          </motion.h1>

          <motion.p
            {...fade(0.16)}
            data-testid="hero-subtitle"
            className="max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg"
          >
            Odontologia premium com conforto, discrição e atendimento personalizado — do primeiro
            contato ao pós-procedimento.
          </motion.p>

          <motion.div {...fade(0.24)} className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <Button
              asChild
              className="h-12 rounded-xl px-6 text-base active:scale-[0.98] transition-[background-color,transform] duration-200"
            >
              <a href="#agendamento" onClick={scrollTo("agendamento")} data-testid="hero-primary-cta-button">
                <CalendarCheck className="h-4 w-4" />
                Solicitar agendamento
              </a>
            </Button>
            <Button
              asChild
              variant="secondary"
              className="h-12 rounded-xl border border-border px-6 text-base active:scale-[0.98] transition-[background-color,transform] duration-200"
            >
              <a
                href={buildWhatsAppUrl()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-button"
              >
                <MessageCircle className="h-4 w-4" />
                Falar no WhatsApp
              </a>
            </Button>
          </motion.div>

          <motion.ul
            {...fade(0.32)}
            className="mt-2 flex flex-wrap gap-x-8 gap-y-3 text-sm text-muted-foreground"
            data-testid="hero-highlights"
          >
            {["Ambiente acolhedor", "Atendimento personalizado", "Estética natural"].map((item) => (
              <li key={item} className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--brand-champagne-gold))]" aria-hidden="true" />
                {item}
              </li>
            ))}
          </motion.ul>
        </div>

        <motion.div
          initial={reduce ? false : { opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1], delay: 0.2 }}
          className="relative lg:col-span-6"
        >
          <div
            aria-hidden="true"
            className="absolute -inset-3 rounded-[calc(var(--radius)+12px)] border border-[hsl(var(--brand-champagne-gold))]/30"
          />
          <figure className="relative overflow-hidden rounded-[var(--radius)] shadow-[0_30px_80px_rgba(20,16,12,0.16)]">
            <img
              src={HERO_IMAGE}
              alt="Interior da clínica RAMALIVE ODONTOLOGIA em tons neutros e sofisticados"
              className="aspect-[4/3] w-full object-cover lg:aspect-[5/4]"
              loading="eager"
              data-testid="hero-image"
            />
          </figure>
          <div className="absolute -bottom-5 left-6 hidden items-center gap-4 rounded-2xl border border-border bg-card/90 px-5 py-4 shadow-[0_14px_40px_rgba(20,16,12,0.10)] backdrop-blur-md sm:flex">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[hsl(var(--brand-beige))] text-[hsl(var(--brand-soft-brown))]">
              <CalendarCheck className="h-5 w-5" />
            </div>
            <div className="leading-tight">
              <p className="text-sm font-semibold text-foreground">Solicite seu horário</p>
              <p className="text-xs text-muted-foreground">Retornamos para confirmar com você</p>
            </div>
          </div>
        </motion.div>
      </div>

      <a
        href="#sobre"
        onClick={scrollTo("sobre")}
        aria-label="Rolar para a seção Sobre"
        data-testid="hero-scroll-indicator"
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 rounded-full p-2 text-muted-foreground transition-colors duration-200 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:block"
      >
        <ArrowDown className="h-5 w-5 animate-bounce" />
      </a>
    </section>
  );
};
